import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Button, cx, Dialog, ErrorText, Spinner } from "./ui.tsx";
import { Icon } from "./icons.tsx";

export interface Picked {
  path: string;
  kind: "file" | "dir";
}

/**
 * Browse the workspace and pick folders and/or files. `mode="folder"` picks one destination folder
 * (the one you're in); `mode="context"` ticks any number of files and folders.
 */
export function WorkspacePicker({
  mode,
  title,
  confirmLabel,
  exclude = [],
  onPick,
  onClose,
}: {
  mode: "context" | "folder";
  title: string;
  confirmLabel: string;
  exclude?: string[];
  onPick: (picked: Picked[]) => void;
  onClose: () => void;
}) {
  const [path, setPath] = useState("");
  const [picked, setPicked] = useState<Picked[]>([]);
  const list = useQuery({ queryKey: ["files", path], queryFn: () => unwrap(api.me.assistant.files.$get({ query: { path } })) });
  const crumbs = path ? path.split("/") : [];
  const toggle = (p: Picked) => setPicked((l) => (l.some((x) => x.path === p.path) ? l.filter((x) => x.path !== p.path) : [...l, p]));
  const items = (list.data?.items ?? []).filter((f) => f.type !== "link" && !exclude.includes(f.path) && (mode === "context" || f.type === "dir"));

  return (
    <Dialog open onClose={onClose} title={title} wide>
      <div className="mb-2 flex flex-wrap items-center gap-1 text-sm">
        <button onClick={() => setPath("")} className="rounded px-1.5 py-0.5 font-medium hover:bg-[var(--panel-2)]">workspace</button>
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="text-[var(--muted)]">/</span>
            <button onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))} className="rounded px-1.5 py-0.5 hover:bg-[var(--panel-2)]">{c}</button>
          </span>
        ))}
      </div>
      <ErrorText error={list.error} />
      <div className="h-80 overflow-y-auto rounded-lg border border-[var(--line)]">
        {list.isPending ? (
          <div className="grid h-full place-items-center"><Spinner /></div>
        ) : !items.length ? (
          <p className="grid h-full place-items-center text-sm text-[var(--muted)]">{mode === "folder" ? "No folders here." : "Empty folder."}</p>
        ) : (
          <ul>
            {items.map((f) => {
              const on = picked.some((x) => x.path === f.path);
              return (
                <li key={f.path} className={cx("flex items-center gap-2 border-b border-[var(--line)] px-3 py-1.5 text-sm last:border-0", on && "bg-[var(--panel-2)]")}>
                  {mode === "context" && <input type="checkbox" checked={on} onChange={() => toggle({ path: f.path, kind: f.type === "dir" ? "dir" : "file" })} />}
                  <button className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => (f.type === "dir" ? setPath(f.path) : toggle({ path: f.path, kind: "file" }))}>
                    <Icon name={f.type === "dir" ? "files" : "file"} size={16} className="shrink-0 text-[var(--muted)]" />
                    <span className="truncate">{f.name}</span>
                    {f.type === "dir" && <Icon name="chevron" size={14} className="ml-auto shrink-0 text-[var(--muted)]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-xs text-[var(--muted)]">
          {mode === "folder" ? `Destination: workspace${path ? "/" + path : ""}` : picked.length ? picked.map((p) => p.path + (p.kind === "dir" ? "/" : "")).join(", ") : "Tick files or folders, or add the folder you're in."}
        </span>
        {mode === "context" && path && !picked.some((p) => p.path === path) && (
          <Button onClick={() => toggle({ path, kind: "dir" })}>Add this folder</Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          disabled={mode === "context" && !picked.length}
          onClick={() => onPick(mode === "folder" ? [{ path, kind: "dir" }] : picked)}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
